import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { formatSliderTimeLabel } from "@/components/AgendaFilters/utils/getSliderOptions";
import { useFacilityLessonNoun } from "@/features/Facility";

import {
  lessonTypeFilterAtom,
  startDateFilterAtom,
  timeOfDayFilterAtom,
} from "../../atoms";
import { useGetLessonAgendaFilterBar_facility$key } from "./__generated__/useGetLessonAgendaFilterBar_facility.graphql";

export const useGetLessonAgendaFilterBar = (
  facilityRef: useGetLessonAgendaFilterBar_facility$key | null
) => {
  const timeOfDayFilter = useAtomValue(timeOfDayFilterAtom);
  const dateFilter = useAtomValue(startDateFilterAtom);
  const lessonTypeFilter = useAtomValue(lessonTypeFilterAtom);

  const facility = useFragment(
    useGetLessonAgendaFilterBarFragmentString,
    facilityRef
  );

  const { singular: lessonNoun } = useFacilityLessonNoun(facility ?? null);

  const facilityLessonTypes =
    facility?.lessonTypes?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) ||
    [];

  const startTime = facility?.startEndTime?.startTime;
  const endTime = facility?.startEndTime?.endTime;

  const lessonTypeDisplay = (lessonTypeId: string | null) => {
    if (!lessonTypeId || facilityLessonTypes.length === 0) {
      return null;
    }

    const lessonType = facilityLessonTypes.find(
      (lessonType) => lessonType.id === lessonTypeId
    );

    return `${lessonType?.name || ""}`;
  };

  const timeOfDayDisplay = (
    timeOfDay: { timeGTE?: string; timeLTE?: string } | null
  ): string | null => {
    if (timeOfDay === null) {
      return null;
    }

    const startTimeString = timeOfDay.timeGTE || startTime || null;
    const endTimeString = timeOfDay.timeLTE || endTime || null;

    if (!startTimeString || !endTimeString) {
      return null;
    }

    const formattedStartTime = formatSliderTimeLabel(startTimeString);
    const formattedEndTime = formatSliderTimeLabel(endTimeString);

    return formattedStartTime && formattedEndTime
      ? `${formattedStartTime} - ${formattedEndTime}`
      : null;
  };

  const dateDisplay = (dateString: string | null) => {
    if (!dateString) {
      return null;
    }

    return `${t`After`} ${format(
      parse(dateString, "yyyy-MM-dd", new Date()),
      "MMM do"
    )}`;
  };

  const agendaFilterBarValues = [
    {
      value: lessonTypeDisplay(lessonTypeFilter?.id ?? null),
      placeholder: `${lessonNoun} ${t`type`}`,
    },
    {
      value: timeOfDayDisplay(timeOfDayFilter ?? null),
      placeholder: t`Time of day`,
    },
    { value: dateDisplay(dateFilter ?? null), placeholder: t`Date` },
  ];

  return agendaFilterBarValues;
};

const useGetLessonAgendaFilterBarFragmentString = graphql`
  fragment useGetLessonAgendaFilterBar_facility on Facility {
    ...useFacilityLessonNoun_facility
    startEndTime {
      startTime
      endTime
    }
    lessonTypes {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
